import { useEffect, useRef, useState } from 'react';

import { MAX_IMAGE_BYTES } from './walletUtils';

interface WalletImageCropperProps {
    source: File;                                   // cualquier imagen (JPG, PNG, ...)
    target: { width: number; height: number };      // medida exacta del PNG resultante
    title: string;
    onCancel: () => void;
    onDone: (file: File) => void;
}

type View = { scale: number; x: number; y: number };   // en píxeles del resultado

const canvasToBlob = (canvas: HTMLCanvasElement) =>
    new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));

// Reduce los tonos de color para bajar el peso del PNG (las fotos pueden pasar de 1 MB)
const posterize = (canvas: HTMLCanvasElement, levels: number) => {
    const ctx = canvas.getContext('2d')!;
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const step = 255 / (levels - 1);
    for (let i = 0; i < data.data.length; i += 4) {
        data.data[i] = Math.round(data.data[i] / step) * step;
        data.data[i + 1] = Math.round(data.data[i + 1] / step) * step;
        data.data[i + 2] = Math.round(data.data[i + 2] / step) * step;
    }
    ctx.putImageData(data, 0, 0);
};

// Recortador con proporción fija: el comercio sube cualquier foto, ajusta el encuadre
// y el navegador genera el PNG con la medida exacta que pide Apple o Google.
export const WalletImageCropper = ({ source, target, title, onCancel, onDone }: WalletImageCropperProps) => {
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [url, setUrl] = useState('');
    const [view, setView] = useState<View>({ scale: 1, x: 0, y: 0 });
    const [minScale, setMinScale] = useState(1);
    const [zoom, setZoom] = useState(1);            // 1 = la imagen cubre todo el recuadro
    const [frameWidth, setFrameWidth] = useState(0);
    const [error, setError] = useState('');
    const [working, setWorking] = useState(false);
    const frameRef = useRef<HTMLDivElement>(null);
    const drag = useRef<{ px: number; py: number; x: number; y: number } | null>(null);

    useEffect(() => {
        const objectUrl = URL.createObjectURL(source);
        const img = new Image();
        img.onload = () => {
            // Escala mínima para cubrir el recuadro; se centra la imagen
            const cover = Math.max(target.width / img.naturalWidth, target.height / img.naturalHeight);
            setMinScale(cover);
            setView({
                scale: cover,
                x: (target.width - img.naturalWidth * cover) / 2,
                y: (target.height - img.naturalHeight * cover) / 2,
            });
            setImage(img);
        };
        img.onerror = () => setError('No se pudo leer la imagen. Use un archivo PNG o JPG.');
        img.src = objectUrl;
        setUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [source]);

    useEffect(() => {
        const frame = frameRef.current;
        if (!frame) return;
        const observer = new ResizeObserver(() => setFrameWidth(frame.clientWidth));
        observer.observe(frame);
        return () => observer.disconnect();
    }, []);

    // La imagen nunca deja huecos dentro del recuadro
    const clamp = (next: View): View => {
        if (!image) return next;
        const w = image.naturalWidth * next.scale;
        const h = image.naturalHeight * next.scale;
        return {
            scale: next.scale,
            x: Math.min(0, Math.max(target.width - w, next.x)),
            y: Math.min(0, Math.max(target.height - h, next.y)),
        };
    };

    const applyZoom = (value: number) => {
        setZoom(value);
        setView(prev => {
            const scale = minScale * value;
            // Se mantiene el centro del recuadro
            const cx = target.width / 2, cy = target.height / 2;
            const ratio = scale / prev.scale;
            return clamp({ scale, x: cx - (cx - prev.x) * ratio, y: cy - (cy - prev.y) * ratio });
        });
    };

    const k = frameWidth / target.width;   // píxeles de pantalla por píxel del resultado

    const onPointerDown = (e: React.PointerEvent) => {
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        drag.current = { px: e.clientX, py: e.clientY, x: view.x, y: view.y };
    };
    const onPointerMove = (e: React.PointerEvent) => {
        if (!drag.current || !k) return;
        const start = drag.current;
        setView(prev => clamp({ ...prev, x: start.x + (e.clientX - start.px) / k, y: start.y + (e.clientY - start.py) / k }));
    };
    const onPointerUp = () => { drag.current = null; };

    const handleDone = async () => {
        if (!image) return;
        setWorking(true);
        setError('');
        const canvas = document.createElement('canvas');
        canvas.width = target.width;
        canvas.height = target.height;
        const ctx = canvas.getContext('2d')!;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(image, view.x, view.y, image.naturalWidth * view.scale, image.naturalHeight * view.scale);

        let blob = await canvasToBlob(canvas);
        for (const levels of [64, 32, 16]) {
            if (blob && blob.size <= MAX_IMAGE_BYTES) break;
            posterize(canvas, levels);
            blob = await canvasToBlob(canvas);
        }
        setWorking(false);

        if (!blob) { setError('No se pudo generar la imagen.'); return; }
        if (blob.size > MAX_IMAGE_BYTES) {
            setError('La imagen recortada pesa más de 1 MB. Pruebe con una imagen más sencilla o con menos detalle.');
            return;
        }
        const baseName = source.name.replace(/\.[^.]+$/, '') || 'banner';
        onDone(new File([blob], `${baseName}_${target.width}x${target.height}.png`, { type: 'image/png' }));
    };

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1070 }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content border-0">
                    <div className="modal-header">
                        <div>
                            <h5 className="modal-title mb-0">{title}</h5>
                            <div className="text-muted small">Arrastre la imagen para encuadrarla. Se generará un PNG de {target.width}×{target.height} px.</div>
                        </div>
                        <button type="button" className="btn-close" onClick={onCancel} disabled={working} />
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger py-2 small">{error}</div>}
                        <div
                            ref={frameRef}
                            className="position-relative overflow-hidden rounded border bg-dark"
                            style={{ width: '100%', aspectRatio: `${target.width} / ${target.height}`, cursor: 'grab', touchAction: 'none', userSelect: 'none' }}
                            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
                        >
                            {image && k > 0 && (
                                <img src={url} alt="" draggable={false}
                                    style={{
                                        position: 'absolute', maxWidth: 'none', pointerEvents: 'none',
                                        left: view.x * k, top: view.y * k,
                                        width: image.naturalWidth * view.scale * k, height: image.naturalHeight * view.scale * k,
                                    }} />
                            )}
                            {!image && !error && (
                                <div className="position-absolute top-50 start-50 translate-middle text-white"><span className="spinner-border spinner-border-sm" /></div>
                            )}
                        </div>
                        <div className="d-flex align-items-center gap-2 mt-3">
                            <i className="bi bi-zoom-out text-muted" />
                            <input type="range" className="form-range" min={1} max={4} step={0.01} value={zoom} disabled={!image || working}
                                onChange={(e) => applyZoom(Number(e.target.value))} aria-label="Zoom" />
                            <i className="bi bi-zoom-in text-muted" />
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={working}>Cancelar</button>
                        <button type="button" className="btn btn-primary" onClick={handleDone} disabled={!image || working}>
                            {working && <span className="spinner-border spinner-border-sm me-1" />}Usar recorte
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
